import { Component, forwardRef, Input, OnInit, OnChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { BaseFormComponent } from '../../base-form.component';
import { FormService } from '../../../../services/form/form.service';
import { ParserService } from '../../../../../../common/angular-shared/services/parser/parser.service';
import { TextMaskConfig } from 'angular2-text-mask';

@Component({
  moduleId: module.id,
  selector: 'basic-text-input',
  templateUrl: './basic-text-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BasicTextInputComponent),
      multi: true,
    },
  ],
})
export class BasicTextInputComponent extends BaseFormComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() propertyName: string;
  @Input() placeholder: string;
  @Input() maxlength: number;
  @Input() inputType: string;
  @Input() parserType: string;
  @Input() formatterType: string;
  @Input() extraClasses: string[];
  @Input() disableConditionally: boolean = false;
  @Input() hideLabel: boolean = false;
  @Input() inputMask?: TextMaskConfig;
  @Input() pattern?: string;
  @Input() formGroupComponent?: boolean; // form group is handling set/get for this value
  @Input() isState: boolean = false;
  @Input() outline: boolean = false;

  defaultMask: TextMaskConfig = { mask: false };

  val: string;
  isError: boolean = false;
  componentId: string = '';
  noLabel: boolean = false;
  onChange;
  onTouched;
  isDisabled = false;

  constructor(protected formService: FormService, protected parserService: ParserService) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.prop = this.propertyName;
    this.componentId = this.prop;
    this.noLabel = this.hideLabel;
    this.updateFromModel();
  }

  ngOnChanges(): void {
    if (this.formGroupComponent) {
      this.prop = this.propertyName;
      this.componentId = this.prop;
    }
  }

  updateFromModel(): void {
    if (!this.formGroupComponent) {
      const value = this.getValue(this.prop);
      this.val = this.formatterType ? this.format(this.formatterType, value) : value;
    }
  }

  protected parse(prop: string, value: string): string {
    const parserType = this.parserType || prop;
    return this.parserService.parse(parserType, value);
  }

  async triggerBase(): Promise<boolean> {
    this.val = this.val.trim();

    this.isError = !(await this.validateAndSet(this.prop, this.val));
    if (!this.isError) {
      this.updateFromModel();
    } else {
      this.setValue(this.prop, undefined);
    }
    return this.isError;
  }

  async trigger(): Promise<boolean> {
    let isError = false;

    if (!this.formGroupComponent) {
      isError = await this.triggerBase();
    }
    if (this.onChange && this.onTouched) {
      this.value = this.val;
    }
    // TODO: Only emit change if !isError? HL-28836
    this.valueChanged.emit();
    return isError;
  }

  onblur(): void {
    if (this.onBlur && this.onBlur.observers.length !== 0) {
      this.onBlur.emit();
    }
  }

  set value(value: string) {
    this.onChange(value);
    this.onTouched();
  }

  get value(): string {
    return this.val;
  }

  registerOnChange(fn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disableConditionally = isDisabled;
  }

  writeValue(value): void {
    this.val = value;
  }

  setErrorMessage(errorMessage: string): void {
    this.isError = true;
    this.errorMessage[this.prop] = errorMessage;
  }
}
